import { useState } from 'react'
import { Dialog } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { Link as ScrollLink } from 'react-scroll'
import { Link as RouterLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa6'
import { SiTiktok } from 'react-icons/si'

const logo = require('../static/images/brand/logo-navbar.webp')
const logoHover = require('../static/images/brand/logo-navbar-gold.webp')

const scrollItems = [
  { name: 'Nuestros Cursos', to: 'cursos' },
  { name: 'Oferta Educativa', to: 'oferta' },
  { name: 'Instalaciones', to: 'campusBuga' },
  { name: 'Misión y Visión', to: 'vision' },
]

const routerItems = []

const socialLinks = [
  {
    Icon: FaFacebook,
    href: 'https://www.facebook.com/LarghettoRMA',
    hoverClass: 'hover:text-blue-500',
    label: 'Facebook',
  },
  {
    Icon: FaInstagram,
    href: 'https://www.instagram.com/larghetto_academia',
    hoverClass: 'hover:text-pink-500',
    label: 'Instagram',
  },
  {
    Icon: SiTiktok,
    href: 'https://www.tiktok.com/@larghetto_rma',
    hoverClass: 'hover:text-white',
    label: 'TikTok',
  },
  {
    Icon: FaYoutube,
    href: 'https://www.youtube.com/@LarghettoRMA',
    hoverClass: 'hover:text-red-500',
    label: 'YouTube',
  },
]

export default function NavBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeItem, setActiveItem] = useState(null)
  const [logoPressed, setLogoPressed] = useState(false)

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black">
      <div className="absolute bottom-0 left-0 right-0 h-[4px]" style={{ background: 'linear-gradient(135deg, #FFE033, #FFB800)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <RouterLink
            to="/"
            className="flex-shrink-0 relative h-8 group select-none"
            style={{ width: `${Math.round(32 * 1127 / 300)}px`, WebkitTouchCallout: 'none' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            onTouchStart={() => setLogoPressed(true)}
            onTouchEnd={() => setLogoPressed(false)}
            onTouchCancel={() => setLogoPressed(false)}
          >
            <img
              src={logo}
              alt="Larghetto"
              draggable={false}
              className={`absolute inset-0 h-full w-auto transition-opacity duration-300 ${logoPressed ? 'opacity-0' : 'opacity-100'} group-hover:opacity-0`}
              style={{ WebkitTouchCallout: 'none', userSelect: 'none' }}
            />
            <img
              src={logoHover}
              alt="Larghetto"
              draggable={false}
              className={`absolute inset-0 h-full w-auto transition-opacity duration-300 ${logoPressed ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100`}
              style={{ WebkitTouchCallout: 'none', userSelect: 'none' }}
            />
          </RouterLink>

          {/* Desktop nav items (center) */}
          <div className="hidden md:flex items-center gap-8">
            {scrollItems.map((item) => (
              <div key={item.name} className="relative">
                <ScrollLink
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={500}
                  to={item.to}
                  onClick={() => setActiveItem(item.name)}
                  onSetActive={() => setActiveItem(item.name)}
                  className="text-white text-sm font-medium hover:text-[#8A2BE2] transition-colors cursor-pointer"
                >
                  {item.name}
                </ScrollLink>
                {activeItem === item.name && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#8A2BE2]"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </div>
            ))}

          </div>

          {/* Right side: Social icons + mobile hamburger */}
          <div className="flex items-center gap-4">
            {/* Social icons (desktop) */}
            <div className="hidden md:flex items-center gap-3">
              {socialLinks.map(({ Icon, href, hoverClass, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className={`text-gray-400 transition-all duration-200 ${hoverClass} hover:scale-110`}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Abrir menú</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu (Headless UI Dialog) */}
      <Dialog
        as="div"
        className="md:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        {/* Overlay */}
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-sm bg-zinc-900 border-l border-white/10 px-6 py-6 overflow-y-auto">
          {/* Panel header */}
          <div className="flex items-center justify-between mb-8">
            <RouterLink to="/" onClick={() => setMobileMenuOpen(false)}>
              <img
                src={logo}
                alt="Larghetto"
                className="h-8 w-auto"
              />
            </RouterLink>
            <button
              type="button"
              className="rounded-md p-2 text-gray-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Cerrar menú</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Nav items */}
          <div className="flex flex-col gap-1">
            {scrollItems.map((item) => (
              <ScrollLink
                key={item.name}
                spy={true}
                smooth={true}
                offset={-70}
                duration={500}
                to={item.to}
                className="block rounded-lg px-3 py-3 text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </ScrollLink>
            ))}
          </div>

          {/* Social icons (mobile) */}
          <div className="mt-10 pt-6 border-t border-white/10">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-4">Síguenos</p>
            <div className="flex items-center gap-5">
              {socialLinks.map(({ Icon, href, hoverClass, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className={`text-gray-400 transition-all duration-200 ${hoverClass} hover:scale-110`}
                >
                  <Icon size={22} />
                </a>
              ))}
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </nav>
  )
}
